import {
  teaserContentTypeValues,
  teaserLayoutLiveblogLinkValues,
  teaserLayoutLiveIndicatorStyleValues,
  teaserLayoutValues,
  teaserLayoutWhenScoreboardValues,
  teaserTimelineValues
} from "@webng/liveblog";
import z from "zod";
import {createPartialParser, removeNullValues, zBooleanWrapper} from "./utils";
import {ParsePayload} from "zod/v4/core";
import {analyticsTeaserThemeKeys} from "./analytics-theme-keys";
import {SharedThemeConfiguration} from "./shared";

// Stored themes may carry a layout value introduced by a newer build (see design.md, Migration Plan);
// dropping it lets the default apply instead of rejecting the whole configuration.
export function preprocessLiveblogTeaserThemeConfiguration(arg: unknown, ctx: ParsePayload): unknown {
  const step1 = removeNullValues(arg, ctx)
  if (step1 && typeof step1 === "object" && "layout" in step1) {
    const {layout, ...rest} = step1 as Record<string, unknown>
    if (!(teaserLayoutValues as readonly string[]).includes(layout as string)) {
      return rest
    }
  }
  return step1
}

export const rawLiveblogTeaserThemeConfigurationSchema = z.object({
  layout: z.enum(teaserLayoutValues).default("slider"),
  layoutLiveIndicatorStyle: z.enum(teaserLayoutLiveIndicatorStyleValues).default("badge"),
  layoutLiveblogLink: z.enum(teaserLayoutLiveblogLinkValues).default("bottom"),
  layoutTimeline: z.enum(teaserTimelineValues).default("time"),
  showTitle: zBooleanWrapper(true),
  styleInvertTime: zBooleanWrapper(),
  layoutWhenScoreboard: z.enum(teaserLayoutWhenScoreboardValues).default("scoreBoardEvents"),
  styleBgColor: z.string().default(""),
  styleBgColorDark: z.string().default(""),
  styleTextColor: z.string().default(""),
  styleTextColorDark: z.string().default(""),
  styleTextMutedColor: z.string().default(""),
  styleTextMutedColorDark: z.string().default(""),
  styleLinkColor: z.string().default(""),
  styleLinkColorDark: z.string().default(""),
  styleSurfaceColor: z.string().default(""),
  styleSurfaceColorDark: z.string().default(""),
  content: z.enum(teaserContentTypeValues).default("highlights"),
  limit: z.coerce.number().int().min(1).max(15).default(8),
})

export type LiveblogTeaserThemeConfiguration = z.infer<typeof rawLiveblogTeaserThemeConfigurationSchema>

export const liveblogTeaserThemeConfigurationSchema = z.preprocess(preprocessLiveblogTeaserThemeConfiguration, rawLiveblogTeaserThemeConfigurationSchema)

export const parsePartialLiveblogTeaserThemeConfiguration = createPartialParser<LiveblogTeaserThemeConfiguration>(rawLiveblogTeaserThemeConfigurationSchema, preprocessLiveblogTeaserThemeConfiguration)

// The subset of teaser theme options reported to analytics on embed load lives in the dependency-free
// ./analytics-theme-keys leaf (shared with the liveblog list, so the bi-mcp catalog can import both without
// the zod graph); re-exported here for back-compat. The `Pick<LiveblogTeaserThemeConfiguration, …>` in
// AnalyticsTeaserThemeConfiguration below is the build-time guard for a renamed/removed option.
export {analyticsTeaserThemeKeys};

// Alongside the resolved option values we carry `colorScheme` (shared config) and `themeId`: the identity
// of the saved theme the teaser selected, or "" when configured inline. `limit` is the one numeric option
// and is stringified so the whole snapshot is assignable to the opaque wire type (string | boolean).
export type AnalyticsTeaserThemeConfiguration =
  Omit<Pick<LiveblogTeaserThemeConfiguration, typeof analyticsTeaserThemeKeys[number]>, "limit">
  & { limit: string }
  & Pick<SharedThemeConfiguration, "colorScheme">
  & { themeId: string }

export function buildAnalyticsTeaserThemeConfiguration(
  teaser: LiveblogTeaserThemeConfiguration,
  shared: Pick<SharedThemeConfiguration, "colorScheme">,
  themeId: string = ""
): AnalyticsTeaserThemeConfiguration {
  // Values are copied verbatim from the already-resolved configuration (enum strings / booleans) so a
  // future option value never needs a matching change on the analytics side; `limit` is stringified.
  const theme = { colorScheme: shared.colorScheme, themeId, limit: String(teaser.limit) } as AnalyticsTeaserThemeConfiguration
  for (const key of analyticsTeaserThemeKeys) {
    if (key === "limit") continue
    (theme as Record<string, unknown>)[key] = teaser[key]
  }
  return theme
}
